/**
 * EditableBlock - 单个可编辑块组件
 * 复刻 trial_demo_editor 的 Block.jsx 渲染结构：
 * outerContainer > contentWrapper > [gutter, selectionWrapper > [bullet, content]]
 */

import React, { useRef, useEffect, useCallback, useState } from 'react';
import { GripVertical, Link as LinkIcon } from 'lucide-react';
import type { EditableBlockProps } from './types';
import { setCursorPosition, getCursorOffset } from './useBlockReducer';
import { WORD_CONTENT_WIDTH_PX } from './blockStyleUtils';
import { useLanguage } from '../../../i18nContext';

const EditableBlock = React.memo(function EditableBlock({
  block,
  index,
  isActive,
  cursorOffset,
  isSelected,
  isDragging,
  isMultiDragOther,
  multiDragCount,
  dragHandleProps,
  dispatch,
  blocks,
  isLinkedActive,
  hasMatch,
  isLineEndpoint,
  onLinkToggle,
  bulletText,
  styleConfig,
}: EditableBlockProps) {
  const { t } = useLanguage();
  const contentRef = useRef<HTMLDivElement>(null);
  const lastContentRef = useRef(block.content);
  const isClickFocusRef = useRef(false);
  const isComposingRef = useRef(false);
  const [hovered, setHovered] = useState(false);

  // ref callback：DOM 元素挂载时立即同步 content
  const setContentRef = useCallback((el: HTMLDivElement | null) => {
    (contentRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
    if (el) {
      const currentText = el.innerText.replace(/\n$/, '');
      if (currentText !== block.content) {
        el.innerText = block.content;
      }
      lastContentRef.current = block.content;
    }
  }, [block.content]);

  // 同步外部 content 变化到 DOM（undo/merge/replace/selection 切换等场景）
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const currentText = el.innerText.replace(/\n$/, '');
    if (currentText !== block.content) {
      el.innerText = block.content;
    }
    lastContentRef.current = block.content;
  }, [block.content, block.id, isDragging, isSelected]);

  // 聚焦管理
  useEffect(() => {
    if (!isActive || isSelected) return;
    const el = contentRef.current;
    if (!el) return;

    const isAlreadyFocused = document.activeElement === el;
    if (!isAlreadyFocused) {
      el.focus();
    }

    if (!isClickFocusRef.current || cursorOffset !== null) {
      try {
        const range = document.createRange();
        const sel = window.getSelection();
        const textNode = el.firstChild || el;
        if (textNode) {
          const len = textNode.nodeType === 3 ? (textNode as Text).length : 0;
          let offset = 0;
          if (cursorOffset === null || cursorOffset === undefined) {
            offset = len;
          } else {
            offset = Math.min(cursorOffset, len);
          }
          if (textNode.nodeType === 3) {
            range.setStart(textNode, offset);
            range.collapse(true);
          } else {
            range.selectNodeContents(el);
            range.collapse(false);
          }
          sel?.removeAllRanges();
          sel?.addRange(range);
        }
      } catch (_e) { /* ignore */ }
    }

    isClickFocusRef.current = false;
  }, [isActive, isSelected, cursorOffset]);

  // 同步内容到 state
  const syncContent = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    const text = el.innerText.replace(/\n$/, '');
    if (text !== lastContentRef.current) {
      lastContentRef.current = text;
      dispatch({ type: 'UPDATE_CONTENT', id: block.id, content: text });
    }
  }, [dispatch, block.id]);

  const handleInput = useCallback((e: React.FormEvent<HTMLDivElement>) => {
    if (isComposingRef.current) return;
    const el = e.target as HTMLDivElement;
    const text = el.innerText.replace(/\n$/, '');
    lastContentRef.current = text;
    // 获取当前光标位置，传给 reducer 以便 undo 时恢复
    const offset = getCursorOffset(el);
    dispatch({ type: 'UPDATE_CONTENT', id: block.id, content: text, cursorOffset: offset });
  }, [dispatch, block.id]);

  const handleCompositionStart = useCallback(() => {
    isComposingRef.current = true;
  }, []);

  const handleCompositionEnd = useCallback((e: React.CompositionEvent<HTMLDivElement>) => {
    isComposingRef.current = false;
    const el = e.target as HTMLDivElement;
    const text = el.innerText.replace(/\n$/, '');
    lastContentRef.current = text;
    const offset = getCursorOffset(el);
    dispatch({ type: 'UPDATE_CONTENT', id: block.id, content: text, cursorOffset: offset });
  }, [dispatch, block.id]);

  const handleBlur = useCallback(() => {
    syncContent();
  }, [syncContent]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (contentRef.current && contentRef.current.contains(e.target as Node)) {
      isClickFocusRef.current = true;
    } else {
      isClickFocusRef.current = false;
    }
  }, []);

  const handleClick = useCallback((e: React.MouseEvent) => {
    // Ctrl+Click: 切换选中
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      dispatch({ type: 'TOGGLE_SELECTION', id: block.id });
      return;
    }
    // Shift+Click: 范围选中
    if (e.shiftKey) {
      e.preventDefault();
      dispatch({ type: 'RANGE_SELECTION', id: block.id });
      return;
    }
    // 普通点击：清除多选，设置活跃
    if (isSelected) {
      dispatch({ type: 'SET_SELECTION', ids: [] });
    }
    isClickFocusRef.current = true;
    dispatch({ type: 'SET_ACTIVE', id: block.id });
  }, [dispatch, block.id, isSelected]);

  const handleGutterClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch({ type: 'TOGGLE_SELECTION', id: block.id });
  }, [dispatch, block.id]);

  // ============ 键盘处理 ============
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (isSelected) return;
    if (isComposingRef.current) return;
    const el = contentRef.current;
    if (!el) return;

    const rawText = el.innerText;
    const safeText = rawText.replace(/\n$/, '');
    const len = safeText.length;
    const sel = window.getSelection();
    const offset = sel?.anchorOffset ?? 0;

    // Enter: 拆分 block
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();

      let prefix: string, suffix: string;
      if (sel && !sel.isCollapsed) {
        const start = Math.min(sel.anchorOffset, sel.focusOffset);
        const end = Math.max(sel.anchorOffset, sel.focusOffset);
        prefix = safeText.slice(0, start);
        suffix = safeText.slice(end);
      } else {
        prefix = safeText.slice(0, offset);
        suffix = safeText.slice(offset);
      }

      lastContentRef.current = prefix;
      el.innerText = prefix;
      dispatch({ type: 'UPDATE_CONTENT', id: block.id, content: prefix });
      dispatch({
        type: 'ADD',
        afterId: block.id,
        content: suffix,
        level: block.level,
        focusNew: true,
      });
      return;
    }

    // Backspace 在行首
    if (e.key === 'Backspace') {
      const isAtStart = sel?.isCollapsed && offset === 0;
      if (isAtStart) {
        if (index === 0) return;
        e.preventDefault();
        const prevBlock = blocks[index - 1];
        if (!safeText) {
          dispatch({ type: 'DELETE', ids: [block.id] });
        } else {
          const mergedContent = prevBlock.content + safeText;
          const cursorPos = prevBlock.content.length;
          dispatch({
            type: 'MERGE',
            destId: prevBlock.id,
            srcId: block.id,
            mergedContent,
            cursorOffset: cursorPos,
          });
        }
        return;
      }
    }

    // Delete 在行尾
    if (e.key === 'Delete') {
      const isAtEnd = sel?.isCollapsed && offset >= len;
      if (isAtEnd) {
        if (index === blocks.length - 1) return;
        e.preventDefault();
        const nextBlock = blocks[index + 1];
        if (!nextBlock.content) {
          dispatch({ type: 'DELETE', ids: [nextBlock.id] });
        } else {
          const mergedContent = safeText + nextBlock.content;
          dispatch({
            type: 'MERGE',
            destId: block.id,
            srcId: nextBlock.id,
            mergedContent,
            cursorOffset: offset,
          });
        }
        return;
      }
    }

    // Tab: 改变层级
    if (e.key === 'Tab') {
      e.preventDefault();
      const newLevel = e.shiftKey
        ? Math.max(1, block.level - 1)
        : Math.min(5, block.level + 1);
      if (newLevel !== block.level) {
        dispatch({ type: 'UPDATE_LEVEL', id: block.id, level: newLevel });
      }
      return;
    }

    // ArrowUp
    if (e.key === 'ArrowUp') {
      if (index === 0) return;
      e.preventDefault();
      syncContent();
      dispatch({ type: 'SET_ACTIVE', id: blocks[index - 1].id, cursorOffset: blocks[index - 1].content.length });
      return;
    }

    // ArrowDown
    if (e.key === 'ArrowDown') {
      if (index === blocks.length - 1) return;
      e.preventDefault();
      syncContent();
      dispatch({ type: 'SET_ACTIVE', id: blocks[index + 1].id, cursorOffset: 0 });
      return;
    }

    // ArrowLeft 在行首
    if (e.key === 'ArrowLeft' && sel?.isCollapsed && offset === 0) {
      if (index === 0) return;
      e.preventDefault();
      syncContent();
      dispatch({ type: 'SET_ACTIVE', id: blocks[index - 1].id, cursorOffset: blocks[index - 1].content.length });
      return;
    }

    // ArrowRight 在行尾
    if (e.key === 'ArrowRight' && sel?.isCollapsed && offset >= len) {
      if (index === blocks.length - 1) return;
      e.preventDefault();
      syncContent();
      dispatch({ type: 'SET_ACTIVE', id: blocks[index + 1].id, cursorOffset: 0 });
      return;
    }
  }, [block, blocks, index, dispatch, syncContent, isSelected]);

  // ============ 渲染 - 拖拽按钮在外层，内容区与 Word 一致 ============
  return (
    <div
      className={`group${isSelected ? ' is-selected-block' : ''}`}
      style={{
        display: 'flex',
        alignItems: 'stretch',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      data-block-id={block.id}
    >
      {/* 拖拽手柄 - 固定在左侧，与内容第一行对齐 */}
      <div
        style={{
          width: '24px',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          cursor: 'grab',
          userSelect: 'none',
          paddingTop: styleConfig.outerContainer.marginTop || '0px',
          position: 'relative',
        }}
        className={`transition-all ${hovered || isSelected ? 'opacity-100' : 'opacity-0 hover:opacity-100'}`}
        onClick={handleGutterClick}
        {...dragHandleProps}
      >
        <GripVertical size={14} className="text-slate-400 group-hover:text-slate-600" style={{ marginTop: '3px' }} />
        {/* 多选拖动计数 badge */}
        {multiDragCount > 1 && isDragging && (
          <div style={{
            position: 'absolute',
            top: '-2px',
            right: '-4px',
            background: '#2563eb',
            color: '#fff',
            fontSize: '10px',
            fontWeight: 700,
            borderRadius: '50%',
            width: '16px',
            height: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 1,
            zIndex: 20,
          }}>
            {multiDragCount}
          </div>
        )}
      </div>

      {/* 内容区 - 与 Word A4 导出完全一致 */}
      <div style={{ width: `${WORD_CONTENT_WIDTH_PX}px`, flexShrink: 0, minWidth: 0, overflow: 'hidden' }}>
        <div style={styleConfig.outerContainer}>
          <div style={styleConfig.contentWrapper}>
            <div
              data-selection-wrapper={block.id}
              style={{
                ...styleConfig.selectionWrapper,
                backgroundColor: isSelected ? 'rgba(35, 131, 226, 0.25)' : (isLinkedActive ? 'rgba(232, 237, 223, 0.5)' : 'transparent'),
                boxShadow: isLinkedActive ? 'inset 0 0 0 1px #4d7c0f' : undefined,
                borderRadius: isLinkedActive ? '4px' : undefined,
                padding: isLinkedActive ? '2px 6px' : styleConfig.selectionWrapper?.padding,
                opacity: isSelected && !isDragging ? 0.6 : 1,
                userSelect: isSelected ? 'none' : 'auto',
              }}
            >
              {bulletText && (
                <div style={styleConfig.bullet}>
                  {bulletText}
                </div>
              )}

              <div
                ref={setContentRef}
                contentEditable={!isSelected}
                suppressContentEditableWarning
                data-placeholder={t.workspace.inputPlaceholder}
                onInput={handleInput}
                onCompositionStart={handleCompositionStart}
                onCompositionEnd={handleCompositionEnd}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                style={styleConfig.content}
              />
            </div>
          </div>
        </div>
      </div>

      {/* 连线按钮 - 固定在右侧，与拖拽手柄对称 */}
      <div
        style={{
          width: '24px',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          paddingTop: styleConfig.outerContainer.marginTop || '0px',
        }}
      >
        {hasMatch && (!isLinkedActive || isLineEndpoint) && onLinkToggle && (
          <button
            data-link-btn={block.id}
            onClick={(e) => {
              e.stopPropagation();
              onLinkToggle(block.id);
            }}
            style={{
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: isLinkedActive ? '2px solid white' : '2px solid #E8EDDF',
              background: isLinkedActive ? '#1a2e05' : 'white',
              color: isLinkedActive ? 'white' : '#a89f91',
              cursor: 'pointer',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              padding: 0,
              marginTop: '3px',
              opacity: isLinkedActive ? 1 : (hovered ? 1 : 0),
              transition: 'opacity 0.15s, background 0.15s',
            }}
            title={t.workspace.viewLinkedSource}
          >
            <LinkIcon size={10} strokeWidth={2.5} />
          </button>
        )}
      </div>
    </div>
  );
});

export default EditableBlock;
